import {
  Activity,
  ArrowRight,
  BookOpen,
  BookOpenCheck,
  Bot,
  CalendarCheck,
  Check,
  ChevronDown,
  CircleCheck,
  CircleDashed,
  CircleX,
  ClipboardList,
  Clock,
  CodeXml,
  Coins,
  ContactRound,
  FileText,
  HandCoins,
  HandHelping,
  Handshake,
  HeartPulse,
  History,
  IdCard,
  Link2,
  ListChecks,
  ListTodo,
  LoaderCircle,
  LucideIcon,
  MessageSquareText,
  MessagesSquare,
  Paperclip,
  PhoneCall,
  Receipt,
  RefreshCw,
  Sparkles,
  StickyNote,
  Target,
  ThumbsDown,
  ThumbsUp,
  TriangleAlert,
  UserRoundSearch,
  Wallet,
  X,
} from 'lucide-react';

import type {
  AiCheckpointStatus,
  AiEvidenceKind,
  AiPortraitCheckpointKey,
  AiPortraitList,
  AiPortraitStatus,
  AiPortraitTextSection,
} from 'core/hooks/AiPortrait/aiPortraitModel';

/**
 * ТЗ 0104, агент 3 — значки ИИ-портрета студента. Одна карта на смысл;
 * полноту держит `__tests__/aiPortraitIcons.test.ts`.
 *
 * Значки сущностей — те же, что у вкладок карточки студента
 * (`StudentTabBar`) и видов связи задачи (`taskLinkKinds`): переписка —
 * `MessagesSquare`, SMS — `MessageSquareText`, задачи — `ListChecks`, опросы
 * — `ClipboardList`, договор — `FileText`, платежи — `Wallet`, счета —
 * `Receipt`, заявка дофинансирования — `UserRoundSearch`.
 */

/** Портрет — тот же знак, что у агента `student_portrait` на `/ai-agent`. */
export const AI_PORTRAIT_ICON: LucideIcon = ContactRound;
/** «AI — wersja robocza». */
export const AI_PORTRAIT_DRAFT_ICON: LucideIcon = Bot;
export const AI_PORTRAIT_REFRESH_ICON: LucideIcon = RefreshCw;
export const AI_PORTRAIT_SUMMARY_ICON: LucideIcon = Sparkles;
/** «Są nowe wydarzenia od ostatniego portretu». */
export const AI_PORTRAIT_STALE_ICON: LucideIcon = History;
/** Финансовое приложение. */
export const AI_PORTRAIT_FINANCE_ICON: LucideIcon = HandCoins;
/** Пункты финансового приложения (деньги, договоры, дофинансирование). */
export const AI_PORTRAIT_FINANCE_ITEMS_ICON: LucideIcon = Coins;

/** ТЗ 0120: «Кто это» — факты о человеке (работа, мотивация, уровень). */
export const AI_PORTRAIT_ABOUT_ICON: LucideIcon = IdCard;
/** ТЗ 0120: «Плюсы» — галочка тона `ok` (строка мини-карточки). */
export const AI_PORTRAIT_STRENGTHS_ICON: LucideIcon = Check;
/** ТЗ 0120: «Минусы» — крестик тона `danger`. */
export const AI_PORTRAIT_CONCERNS_ICON: LucideIcon = X;
/** ТЗ 0120: заголовки блоков «Плюсы» / «Минусы» — крупный знак в круге. */
export const AI_PORTRAIT_STRENGTHS_BLOCK_ICON: LucideIcon = ThumbsUp;
export const AI_PORTRAIT_CONCERNS_BLOCK_ICON: LucideIcon = ThumbsDown;
/** ТЗ 0120: маркеры строк «Плюсы» / «Минусы» в таблице. */
export const AI_PORTRAIT_STRENGTH_ROW_ICON: LucideIcon = CircleCheck;
export const AI_PORTRAIT_CONCERN_ROW_ICON: LucideIcon = CircleX;
/** ТЗ 0120: компактный чип «N источн.» со списком источников. */
export const AI_PORTRAIT_EVIDENCE_ICON: LucideIcon = Paperclip;
/** ТЗ 0120: «Подробно» — раскрытие текстовых разделов. */
export const AI_PORTRAIT_DETAILS_ICON: LucideIcon = ChevronDown;
/** ТЗ 0120: «Открыть портрет →» на тизере «Обзора». */
export const AI_PORTRAIT_OPEN_ICON: LucideIcon = ArrowRight;
/** ТЗ 0120: заголовок блока чекпоинтов. */
export const AI_PORTRAIT_CHECKPOINTS_ICON: LucideIcon = ListChecks;

/**
 * ТЗ 0120: статус чекпоинта → значок. Зелёная галочка, жёлтый треугольник,
 * красный крест, пунктирный круг «модель не знает».
 */
export const AI_CHECKPOINT_STATUS_ICONS: Readonly<
  Record<AiCheckpointStatus, LucideIcon>
> = {
  ok: CircleCheck,
  warn: TriangleAlert,
  bad: CircleX,
  unknown: CircleDashed,
};

/** ТЗ 0120: чекпоинт → значок темы (в заголовке карточки чекпоинта). */
export const AI_CHECKPOINT_ICONS: Readonly<
  Record<AiPortraitCheckpointKey, LucideIcon>
> = {
  motivation: Target,
  funding_need: HandCoins,
  tech_level: CodeXml,
  engagement: Activity,
  attendance: CalendarCheck,
  payment: Wallet,
};

/** Статус портрета → значок метки (как у статусов прогона на `/ai-agent`). */
export const AI_PORTRAIT_STATUS_ICONS: Readonly<
  Record<AiPortraitStatus, LucideIcon>
> = {
  none: CircleDashed,
  queued: Clock,
  running: LoaderCircle,
  ready: CircleCheck,
  failed: CircleX,
};

/** Текстовый раздел → значок заголовка. */
export const AI_PORTRAIT_SECTION_ICONS: Readonly<
  Record<AiPortraitTextSection, LucideIcon>
> = {
  learning: BookOpen,
  engagement: Activity,
  sentimentTrend: HeartPulse,
  communication: MessagesSquare,
};

/** Список → значок заголовка. */
export const AI_PORTRAIT_LIST_ICONS: Readonly<
  Record<AiPortraitList, LucideIcon>
> = {
  risks: TriangleAlert,
  openNeeds: HandHelping,
  nextActions: ListTodo,
};

/** Вид доказательства → значок чипа. */
export const AI_EVIDENCE_ICONS: Readonly<Record<AiEvidenceKind, LucideIcon>> = {
  message: MessagesSquare,
  sms: MessageSquareText,
  note: StickyNote,
  task: ListChecks,
  attendance: CalendarCheck,
  mark: BookOpenCheck,
  survey: ClipboardList,
  agreement: FileText,
  payment: Wallet,
  invoice: Receipt,
  funding: UserRoundSearch,
  aplora_lead: Handshake,
  aplora_call: PhoneCall,
};

/** Вид, которого экран ещё не знает, — нейтральное звено, а не пустота. */
export const AI_EVIDENCE_FALLBACK_ICON: LucideIcon = Link2;

export const aiEvidenceIconOf = (kind: string): LucideIcon =>
  (AI_EVIDENCE_ICONS as Record<string, LucideIcon>)[kind] ??
  AI_EVIDENCE_FALLBACK_ICON;

/** Незнакомый ключ чекпоинта — то же нейтральное звено. */
export const aiCheckpointIconOf = (key: string): LucideIcon =>
  (AI_CHECKPOINT_ICONS as Record<string, LucideIcon>)[key] ??
  AI_EVIDENCE_FALLBACK_ICON;
